/**
 * App.tsx — v4 routing skeleton
 *
 * React Router v6. SorenHome (via AriaFloatGate) mounts at App root so it
 * persists across ALL routes, including /projects/new which renders WITHOUT V3Shell.
 *
 * Route → shell mapping:
 *   /dashboard             → ContractorRoute + V3Shell + DashboardScreen       (S0)
 *   /projects              → ContractorRoute + V3Shell + ProjectsListScreen     (S2)
 *   /projects/new          → ContractorRoute + WizardShell                      (S1)
 *   /projects/:id          → redirect to /projects?pm=<id>
 *   /settings              → ContractorRoute + V3Shell + SettingsScreen         (S4)
 *   /settings/aria         → ContractorRoute + V3Shell + AriaSetupScreen        (S5)
 *   /aria/follow-ups       → ContractorRoute + V3Shell + FollowUpsScreen
 *   /subs                  → ContractorRoute + V3Shell + SubsVendorsScreen
 *   /subs/:id/trust        → ContractorRoute + V3Shell + TrustDetailScreen (S3)
 *
 *   /vendor/dashboard      → VendorRoute + VendorShell + VendorDashboardScreen
 *   /vendor/projects/:id   → VendorRoute + VendorShell + VendorProjectDetail
 *   /vendor/documents      → VendorRoute + VendorShell + VendorDocumentsScreen
 *   /vendor/notifications  → VendorRoute + VendorShell + placeholder
 *   /vendor/profile        → VendorRoute + VendorShell + VendorProfileScreen
 *
 * Auth: wrapped in AuthProvider (main.tsx). Role-based guards read from
 * AuthContext so contractors can't access /vendor/* and vendors can't
 * access contractor routes.
 *
 * Special case: /vendor/accept-invite[/*] intercept happens BEFORE
 * BrowserRouter mounts (no /v4/ prefix, served directly by Express).
 */

import { useQuery } from '@tanstack/react-query'
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import { ServiceWorkerUpdateToast } from './lib/serviceWorkerUpdate'
import { fetchVendorNotifications } from './api/vendor'
import VendorAcceptInvite     from './screens/vendor/VendorAcceptInvite'
import VendorDashboardScreen  from './screens/vendor/VendorDashboardScreen'
import VendorProjectDetail    from './screens/vendor/VendorProjectDetail'
import VendorDocumentsScreen  from './screens/vendor/VendorDocumentsScreen'
import VendorProfileScreen    from './screens/vendor/VendorProfileScreen'
import VendorLogin            from './screens/vendor/VendorLogin'
import VendorForgotPassword   from './screens/vendor/VendorForgotPassword'
import VendorResetPassword    from './screens/vendor/VendorResetPassword'
import ContractorLogin            from './screens/auth/ContractorLogin'
import ContractorRegister         from './screens/auth/ContractorRegister'
import ContractorForgotPassword   from './screens/auth/ContractorForgotPassword'
import ContractorResetPassword    from './screens/auth/ContractorResetPassword'
import PortalChoiceScreen         from './screens/auth/PortalChoiceScreen'
import V3Shell                from './components/shell/V3Shell'
import VendorShell            from './components/shell/VendorShell'
import AriaFloatGate          from './components/shell/AriaFloatGate'
import MirrorBanner           from './components/shell/MirrorBanner'
import VendorRoute            from './components/guards/VendorRoute'
import ContractorAppRoute     from './components/guards/ContractorAppRoute'
import PricingRoute           from './components/guards/PricingRoute'
import { DashboardScreen }    from './screens/dashboard/DashboardScreen'
import ProjectsListScreen     from './screens/projects/ProjectsListScreen'
import PayAppEditorScreen     from './screens/projects/PayAppEditorScreen'
import WizardShell            from './screens/projects-new/WizardShell'
import SettingsScreen         from './screens/settings/SettingsScreen'
import SubsVendorsScreen      from './screens/subs/SubsVendorsScreen'
import TrustDetailScreen      from './screens/subs/TrustDetailScreen'
import AriaSetupScreen        from './screens/settings/AriaSetupScreen'
import FollowUpsScreen        from './screens/aria/FollowUpsScreen'
import CashForecastScreen     from './screens/cash/CashForecastScreen'
import OperationsReportScreen from './screens/reports/OperationsReportScreen'
import ReportsHubScreen       from './screens/reports/ReportsHubScreen'
import OwnerStatementScreen   from './screens/statements/OwnerStatementScreen'
import CashReportScreen       from './features/cashReport/CashReportScreen'
import InviteAcceptScreen     from './screens/invite/InviteAcceptScreen'
import PulseScreen            from './features/pulse/PulseScreen'
import SuperAdminRoute        from './components/guards/SuperAdminRoute'
import AdminCustomersScreen   from './screens/admin/AdminCustomersScreen'
import AdminHelpGapsScreen    from './screens/admin/AdminHelpGapsScreen'
import AdminAgentActivityScreen from './screens/admin/AdminAgentActivityScreen'
import AdminHealthDigestScreen from './screens/admin/AdminHealthDigestScreen'
import SubscriptionPricingScreen from './screens/billing/SubscriptionPricingScreen'
import BillingReturnScreen from './screens/billing/BillingReturnScreen'

/** Redirect /projects/:id → /projects?pm=<id> (preserve focus / sourceContext). */
function ProjectIdRedirect() {
  const { id } = useParams<{ id: string }>()
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  if (id) params.set('pm', id)
  return <Navigate to={`/projects?${params.toString()}`} replace />
}

/** Vendor notifications — document review alerts from contractor */
function VendorNotificationsPlaceholder() {
  const { data: items, isLoading, isError } = useQuery({
    queryKey:  ['vendor-notifications'],
    queryFn:   fetchVendorNotifications,
    staleTime: 30_000,
  })

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '0 4px' }}>
      <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', margin: '0 0 4px' }}>
        Notifications
      </h1>
      <p style={{ fontSize: 13, color: 'var(--ink4)', margin: '0 0 24px' }}>
        Project updates and document status changes
      </p>
      {isLoading && (
        <div className="card" style={{ padding: '20px 16px', fontSize: 13, color: 'var(--ink4)' }}>
          Loading…
        </div>
      )}
      {isError && (
        <div className="card" style={{ padding: '20px 16px', fontSize: 13, color: 'var(--red)' }}>
          Failed to load notifications.
        </div>
      )}
      {!isLoading && !isError && (items ?? []).length === 0 && (
        <div className="card" style={{ padding: '40px 20px', textAlign: 'center' }}>
          <p style={{ fontSize: 14, color: 'var(--ink4)', margin: 0 }}>
            No new notifications
          </p>
        </div>
      )}
      {(items ?? []).map(n => (
        <div key={n.id} className="card" style={{ padding: '14px 16px', marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>
            {n.title}
          </div>
          {n.body && (
            <div style={{ fontSize: 12, color: 'var(--ink3)', marginBottom: 6, lineHeight: 1.5 }}>
              {n.body}
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--ink5)' }}>
            {n.project_name ?? 'Project'} · {new Date(n.created_at).toLocaleDateString()}
          </div>
        </div>
      ))}
    </div>
  )
}

export default function App() {
  // Intercept /vendor/accept-invite and /vendor/accept-invite/* BEFORE BrowserRouter mounts.
  // These routes have no /v4/ prefix — they live at the root.
  // Bare path = code-entry destination from invite email (no token).
  // Cannot use useNavigate inside — redirect via window.location.href.
  const _vaiPath = window.location.pathname
  if (_vaiPath === '/vendor/accept-invite' || _vaiPath.startsWith('/vendor/accept-invite/')) {
    return <VendorAcceptInvite />
  }

  return (
    <BrowserRouter>
      {/*
       * SorenHome (AriaFloatGate) mounted at App root — outside all routes.
       * Always on for non-vendor users.
       */}
      <MirrorBanner />
      <AriaFloatGate />
      <ServiceWorkerUpdateToast />

      <Routes>
        {/* / -> redirect to /dashboard (ContractorRoute will redirect vendors to /vendor/dashboard) */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Public contractor login — no auth required */}
        <Route path="/login" element={<ContractorLogin />} />
        <Route path="/invite/accept" element={<InviteAcceptScreen />} />
        <Route path="/auth/portal-choice" element={<PortalChoiceScreen />} />
        <Route path="/register" element={<ContractorRegister />} />
        <Route path="/signup" element={<Navigate to="/register" replace />} />
        <Route path="/forgot-password" element={<ContractorForgotPassword />} />
        <Route path="/reset-password" element={<ContractorResetPassword />} />

        {/* ── CONTRACTOR ROUTES ─────────────────────────────────────── */}

        {/* Post-signup subscription pricing (checkout_required grant) */}
        <Route path="/pricing" element={
          <PricingRoute>
            <SubscriptionPricingScreen />
          </PricingRoute>
        } />

        <Route path="/billing/return" element={<BillingReturnScreen />} />

        {/* Super admin — customers / billing (user 22 / ADMIN_EMAILS) */}
        <Route path="/admin/customers" element={
          <SuperAdminRoute>
            <V3Shell><AdminCustomersScreen /></V3Shell>
          </SuperAdminRoute>
        } />
        <Route path="/admin/help-gaps" element={
          <SuperAdminRoute>
            <V3Shell><AdminHelpGapsScreen /></V3Shell>
          </SuperAdminRoute>
        } />
        <Route path="/admin/agent-activity" element={
          <SuperAdminRoute>
            <V3Shell><AdminAgentActivityScreen /></V3Shell>
          </SuperAdminRoute>
        } />
        <Route path="/admin/health" element={
          <SuperAdminRoute>
            <V3Shell><AdminHealthDigestScreen /></V3Shell>
          </SuperAdminRoute>
        } />

        {/* S0: Dashboard — canonical lines 578–721 (PR-V4-DASHBOARD) */}
        <Route path="/dashboard" element={
          <ContractorAppRoute><DashboardScreen /></ContractorAppRoute>
        } />

        {/* Soren deep links from weekly cash email */}
        <Route path="/soren" element={
          <ContractorAppRoute><DashboardScreen /></ContractorAppRoute>
        } />

        <Route path="/cash/forecast" element={
          <ContractorAppRoute><CashForecastScreen /></ContractorAppRoute>
        } />

        {/* S2: Projects list — canonical lines 1303–1939 (PR-V4-PROJECTS-LIST) */}
        <Route path="/projects" element={
          <ContractorAppRoute><ProjectsListScreen /></ContractorAppRoute>
        } />

        {/* S1: New Project wizard — inside V3Shell so sidebar nav stays visible */}
        <Route path="/projects/new" element={
          <ContractorAppRoute><WizardShell /></ContractorAppRoute>
        } />

        {/* Pay app editor — full page inside V3Shell so top nav stays visible */}
        <Route path="/projects/:projectId/pay-apps/:payAppId" element={
          <ContractorAppRoute><PayAppEditorScreen /></ContractorAppRoute>
        } />

        <Route path="/projects/:projectId/pulse" element={
          <ContractorAppRoute><PulseScreen /></ContractorAppRoute>
        } />

        {/* S2: Project modal via URL param — /projects/:id → /projects?pm=<id> */}
        <Route path="/projects/:id" element={<ProjectIdRedirect />} />

        {/* S4: Settings — canonical lines 2014–2232 (PR-V4-SETTINGS) */}
        <Route path="/settings" element={
          <ContractorAppRoute><SettingsScreen /></ContractorAppRoute>
        } />

        {/* S5: ARIA setup — canonical lines 2233–2414 */}
        <Route path="/settings/aria" element={
          <ContractorAppRoute><AriaSetupScreen /></ContractorAppRoute>
        } />

        {/* ARIA follow-up drafts — approval-gated collection emails */}
        <Route path="/aria/follow-ups" element={
          <ContractorAppRoute><FollowUpsScreen /></ContractorAppRoute>
        } />

        <Route path="/cash-report" element={
          <ContractorAppRoute><CashReportScreen /></ContractorAppRoute>
        } />

        {/* Operations report — full-width billing KPIs (PR8) */}
        <Route path="/reports" element={
          <ContractorAppRoute><ReportsHubScreen /></ContractorAppRoute>
        } />

        <Route path="/reports/operations" element={
          <ContractorAppRoute><OperationsReportScreen /></ContractorAppRoute>
        } />

        <Route path="/reports/owner-statement" element={
          <ContractorAppRoute><OwnerStatementScreen /></ContractorAppRoute>
        } />

        {/* Owner statement — multi-project (PR10) */}
        <Route path="/statements/owner" element={
          <ContractorAppRoute><OwnerStatementScreen /></ContractorAppRoute>
        } />

        {/* Subs & Vendors — workspace address book */}
        <Route path="/subs" element={
          <ContractorAppRoute><SubsVendorsScreen /></ContractorAppRoute>
        } />

        {/* S3: Sub trust detail — canonical lines 1940–2013 */}
        <Route path="/subs/:id/trust" element={
          <ContractorAppRoute><TrustDetailScreen /></ContractorAppRoute>
        } />

        {/* ── VENDOR ROUTES ─────────────────────────────────────────── */}

        {/* Public — no auth required */}
        <Route path="/vendor/login" element={<VendorLogin />} />
        <Route path="/vendor/forgot-password" element={<VendorForgotPassword />} />
        <Route path="/vendor/reset/:token" element={<VendorResetPassword />} />
        <Route path="/vendor/portal" element={<Navigate to="/vendor/dashboard" replace />} />

        <Route path="/vendor/dashboard" element={
          <VendorRoute>
            <VendorShell><VendorDashboardScreen /></VendorShell>
          </VendorRoute>
        } />

        <Route path="/vendor/projects/:id" element={
          <VendorRoute>
            <VendorShell><VendorProjectDetail /></VendorShell>
          </VendorRoute>
        } />

        <Route path="/vendor/documents" element={
          <VendorRoute>
            <VendorShell><VendorDocumentsScreen /></VendorShell>
          </VendorRoute>
        } />

        <Route path="/vendor/notifications" element={
          <VendorRoute>
            <VendorShell><VendorNotificationsPlaceholder /></VendorShell>
          </VendorRoute>
        } />

        <Route path="/vendor/profile" element={
          <VendorRoute>
            <VendorShell><VendorProfileScreen /></VendorShell>
          </VendorRoute>
        } />
      </Routes>
    </BrowserRouter>
  )
}
